import _ from 'underscore';
import React, { useCallback, useContext, useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { CampaignCreationMethodSelection } from '@bingads-webui-campaign-react/campaign-creation-method-selection';

import { Tile } from './tile';
import { GoalSelection } from './goals/goal-selection';
import { WebsiteAndBusiness } from './website/website-and-business';
import { WebsiteAndSmartPages } from './website/website-and-smart-pages';

import { getStyles } from './styles';
import { VIEWS } from './consts';
import { LanguageCountrySelectionTile } from './language/language-and-country-selection-tile';
import { WebsiteOnlyOption } from './website/website-only-option';
import { OPTIONS } from './website/consts';
import { CreationMethodSubTitle } from './creation-method/creation-method-sub-title';
import { getInitStep } from './helper';
import type { GoalsAndWebsiteTileProps } from './types';

export const GoalsAndWebsiteTile = (props: GoalsAndWebsiteTileProps) => {
  const {
    selectedCampaignGoals,
    onCampaignGoalSelectionChange,
    website,
    setWebsite,
    businessName,
    setBusinessName,
    existingWebsiteSelected,
    setExistingWebsiteSelected,
    setIsValid,
    isWebsiteSocialPlatform,
    setIsWebsiteSocialPlatform,
    unsupportedDomainMessage,
    goalTitle,
    goalsSubtitle,
    websiteSubtitle,
    onNext,
    withBusinessName,
    multiSelect,
    goalTileFooter,
    languages,
    selectedLanguage,
    onLanguageSelectionChange,
    countryOptions,
    selectedCountry,
    onCountrySelected,
    initCountryCode,
    shouldShowCountryChangedWarning,
    initStep,
    campaign,
    useConfirmDialog,
    isSignUp,
    shouldShowSmartPageOption,
    smartPageErrors,
    isLanguageEnabled,
    uiActivity,
    shouldShowLanguageChangedWarning,
    setIsCampaignDeleteConfirmed,
    languageSubtitle,
    isCreationMethodEnabled,
    creationMethodTitle,
    onClickGoogleImport,
    onClickFacebookImport,
    onClickCreateNewCampaign,
  } = props;
  const i18n = useContext(I18nContext);
  const classes = useDefaultStyles(getStyles);

  const [currentView, setView] = useState(getInitStep({ customizedInitStep: initStep, isCreationMethodEnabled }));
  const [alwaysShowError, setAlwaysShowError] = useState(false);
  const [goalError, setGoalError] = useState<string>();
  const hasLanguageSelection
    = useMemo(() => isLanguageEnabled && _.isArray(languages) && _.size(languages) > 1, [languages, isLanguageEnabled]);

  // Currently we want to show the switch to expert footer in all steps if we have goals/language/website
  // because it's needed for the first two, and it'd feel inconsistent if website step doesn't have it.
  // If we only have two steps goals/website, we'll keep it only in the first step
  const showSwitchToExpertTile = isLanguageEnabled || (currentView !== VIEWS.WEBSITE && currentView !== VIEWS.CREATION_METHOD);

  const onCreationMethodNext = useCallback(() => {
    if (_.isFunction(onClickCreateNewCampaign)) {
      onClickCreateNewCampaign();
    }
    setView(VIEWS.GOALS);
  }, [onClickCreateNewCampaign]);

  const onGoalNext = useCallback(() => {
    if (!_.isEmpty(selectedCampaignGoals)) {
      setView(hasLanguageSelection ? VIEWS.LANGUAGE : VIEWS.WEBSITE);
    } else {
      setGoalError(multiSelect ?
        i18n.getString(_TL_('Please select at least one goal.'))
        : i18n.getString(_TL_('Please select a goal.')));
    }
  }, [hasLanguageSelection, i18n, multiSelect, selectedCampaignGoals]);

  const onGoalBack = useCallback(() => {
    setView(VIEWS.CREATION_METHOD);
  }, []);

  useEffect(() => {
    if (!_.isEmpty(selectedCampaignGoals) && goalError) {
      setGoalError(null);
    }
  }, [goalError, selectedCampaignGoals]);

  const onWebsiteBack = useCallback(() => {
    setView(hasLanguageSelection ? VIEWS.LANGUAGE : VIEWS.GOALS);
  }, [hasLanguageSelection]);

  const onWebsiteNext = useCallback(() => {
    setAlwaysShowError(true);
    onNext();
  }, [onNext]);

  const creationMethodSubTitle = useMemo(() => (
    <CreationMethodSubTitle i18n={i18n} />
  ), [i18n]);

  const getWebsiteOptions = useCallback(() => {
    if (shouldShowSmartPageOption) {
      const websiteOptionType = isSignUp ? OPTIONS.SIGNUP_EXISTING_WEBSITE : OPTIONS.USE_MY_WEBSITE;
      const smartPageOptionType = isSignUp ? OPTIONS.SIGNUP_SMART_PAGES : OPTIONS.USE_SMART_PAGES;

      return (<WebsiteAndSmartPages
        website={website}
        setWebsite={setWebsite}
        existingWebsiteSelected={existingWebsiteSelected}
        setExistingWebsiteSelected={setExistingWebsiteSelected}
        setIsValid={setIsValid}
        isWebsiteSocialPlatform={isWebsiteSocialPlatform}
        setIsWebsiteSocialPlatform={setIsWebsiteSocialPlatform}
        unsupportedDomainMessage={unsupportedDomainMessage}
        alwaysShowError={alwaysShowError}
        websiteOptionType={websiteOptionType}
        smartPageOptionType={smartPageOptionType}
        smartPageErrors={smartPageErrors}
        isShowIcon={!isCreationMethodEnabled}
        smartPageLearnMoreLink={isSignUp ? 'https://help.ads.microsoft.com/#apex/ads/en/60126/0' : 'https://help.ads-int.microsoft.com/#apex/ads/en/60128/0'}
      />);
    }
    // user doesn't have smart page setup. We only ask for
    // website details
    return (<WebsiteOnlyOption
      website={website}
      setWebsite={setWebsite}
      setIsValid={setIsValid}
      alwaysShowError={alwaysShowError}
      isWebsiteSocialPlatform={isWebsiteSocialPlatform}
      setIsWebsiteSocialPlatform={setIsWebsiteSocialPlatform}
      unsupportedDomainMessage={unsupportedDomainMessage}
    />);
  }, [
    alwaysShowError, existingWebsiteSelected, isCreationMethodEnabled, isSignUp,
    isWebsiteSocialPlatform, setExistingWebsiteSelected, setIsValid, setIsWebsiteSocialPlatform,
    setWebsite, shouldShowSmartPageOption, smartPageErrors, unsupportedDomainMessage, website,
  ]);

  return (
    <div className={classNames('goals-and-website', classes.container)}>
      {
        currentView === VIEWS.CREATION_METHOD &&
        <Tile
          title={creationMethodTitle}
          subtitle={creationMethodSubTitle}
        >
          <CampaignCreationMethodSelection
            i18n={i18n}
            onClickCreateNewCampaign={onCreationMethodNext}
            onClickGoogleImport={onClickGoogleImport}
            onClickFacebookImport={onClickFacebookImport}
          />
        </Tile>
      }

      {currentView === VIEWS.GOALS &&
        <Tile
          title={goalTitle}
          subtitle={goalsSubtitle}
          onNext={onGoalNext}
          onBack={isCreationMethodEnabled && onGoalBack}
        >
          <GoalSelection
            selectedCampaignGoals={selectedCampaignGoals}
            onCampaignGoalSelectionChange={onCampaignGoalSelectionChange}
            multiSelect={multiSelect}
            isShowIcon={!isCreationMethodEnabled}
          />
          {goalError && <p className={classNames('goals-error', classes.error)}>{goalError}</p>}
        </Tile>
      }
      {isLanguageEnabled && currentView === VIEWS.LANGUAGE && (
        <LanguageCountrySelectionTile
          selectedLanguage={selectedLanguage}
          onLanguageSelectionChange={onLanguageSelectionChange}
          campaign={campaign}
          onNext={setView}
          onBack={setView}
          languages={languages}
          countryOptions={countryOptions}
          selectedCountry={selectedCountry}
          onCountrySelected={onCountrySelected}
          initCountryCode={initCountryCode}
          isCountrySelectDisabled={!isSignUp}
          shouldShowCountryChangedWarning={shouldShowCountryChangedWarning}
          useConfirmDialog={useConfirmDialog}
          uiActivity={uiActivity}
          shouldShowLanguageChangedWarning={shouldShowLanguageChangedWarning}
          setIsCampaignDeleteConfirmed={setIsCampaignDeleteConfirmed}
          subtitle={languageSubtitle}
        />
      )}
      {currentView === VIEWS.WEBSITE &&
        <Tile
          title={i18n.getString(_TL_('Tell us about your business'))}
          subtitle={websiteSubtitle}
          onNext={onWebsiteNext}
          onBack={onWebsiteBack}
        >
          {withBusinessName ?
            <WebsiteAndBusiness
              website={website}
              setWebsite={setWebsite}
              businessName={businessName}
              setBusinessName={setBusinessName}
              setIsValid={setIsValid}
              alwaysShowError={alwaysShowError}
            />
            : getWebsiteOptions()
          }
        </Tile>
      }
      {showSwitchToExpertTile && goalTileFooter}
    </div>);
};

GoalsAndWebsiteTile.propTypes = {
  selectedCampaignGoals: PropTypes.arrayOf(PropTypes.string).isRequired,
  uiActivity: PropTypes.shape({}),
  onCampaignGoalSelectionChange: PropTypes.func.isRequired,
  website: PropTypes.string,
  setWebsite: PropTypes.func.isRequired,
  businessName: PropTypes.string,
  setBusinessName: PropTypes.func,
  existingWebsiteSelected: PropTypes.bool,
  setExistingWebsiteSelected: PropTypes.func,
  setIsValid: PropTypes.func,
  isWebsiteSocialPlatform: PropTypes.bool,
  setIsWebsiteSocialPlatform: PropTypes.func,
  unsupportedDomainMessage: PropTypes.string,
  goalTitle: PropTypes.string.isRequired,
  goalsSubtitle: PropTypes.node.isRequired,
  websiteSubtitle: PropTypes.node.isRequired,
  onNext: PropTypes.func.isRequired,
  withBusinessName: PropTypes.bool,
  multiSelect: PropTypes.bool,
  goalTileFooter: PropTypes.node,
  languages: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string, text: PropTypes.string })),
  selectedLanguage: PropTypes.string,
  onLanguageSelectionChange: PropTypes.func,
  countryOptions: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string, text: PropTypes.string })),
  selectedCountry: PropTypes.string,
  onCountrySelected: PropTypes.func,
  initCountryCode: PropTypes.string,
  shouldShowCountryChangedWarning: PropTypes.bool,
  initStep: PropTypes.string,
  campaign: PropTypes.shape({}),
  useConfirmDialog: PropTypes.func,
  isLanguageEnabled: PropTypes.bool,
  isSignUp: PropTypes.bool,
  shouldShowSmartPageOption: PropTypes.bool,
  isCreationMethodEnabled: PropTypes.bool,
  creationMethodTitle: PropTypes.string,
  onClickGoogleImport: PropTypes.func,
  onClickFacebookImport: PropTypes.func,
  onClickCreateNewCampaign: PropTypes.func,
  smartPageErrors: PropTypes.arrayOf(PropTypes.string),
  shouldShowLanguageChangedWarning: PropTypes.bool,
  setIsCampaignDeleteConfirmed: PropTypes.func,
  languageSubtitle: PropTypes.node,
};

GoalsAndWebsiteTile.defaultProps = {
  website: undefined,
  businessName: undefined,
  existingWebsiteSelected: true,
  useConfirmDialog: null,
  isLanguageEnabled: false,
  shouldShowLanguageChangedWarning: false,
  setExistingWebsiteSelected: _.noop,
  setIsCampaignDeleteConfirmed: _.noop,
  setBusinessName: _.noop,
  setIsValid: _.noop,
  setIsWebsiteSocialPlatform: _.noop,
  unsupportedDomainMessage: undefined,
  isWebsiteSocialPlatform: false,
  withBusinessName: false,
  multiSelect: true,
  goalTileFooter: null,
  uiActivity: null,
  languages: [],
  selectedLanguage: null,
  onLanguageSelectionChange: _.noop,
  countryOptions: null,
  selectedCountry: null,
  onCountrySelected: _.noop,
  initCountryCode: null,
  shouldShowCountryChangedWarning: false,
  initStep: null,
  campaign: null,
  isSignUp: true,
  shouldShowSmartPageOption: false,
  isCreationMethodEnabled: false,
  creationMethodTitle: '',
  onClickGoogleImport: _.noop,
  onClickFacebookImport: _.noop,
  onClickCreateNewCampaign: _.noop,
  smartPageErrors: [],
  languageSubtitle: null,
};
